import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Link2, X, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

export default function BookPreview() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}/preview`);
        setData(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [bookId]);

  // Build the ordered list of "pages"
  const pages = [];
  if (data) {
    pages.push({ type: "cover" });
    if (data.synopsis) pages.push({ type: "synopsis" });
    (data.chapters || []).forEach((c) => pages.push({ type: "chapter", chapter: c }));
    if (pages.length === 1) pages.push({ type: "empty" });
  }
  const total = pages.length;

  const go = useCallback(
    (d) => {
      setDir(d);
      setPage((p) => Math.min(Math.max(p + d, 0), total - 1));
    },
    [total]
  );

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copiato! Condividi la tua anteprima.");
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6">
        <Logo className="mb-8" />
        <p className="font-serif text-3xl font-light text-white">Anteprima non disponibile</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#D4AF37]" />
      </div>
    );
  }

  const p = pages[page];

  return (
    <div className="relative flex min-h-screen flex-col bg-ink" data-testid="book-preview-page">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
        <Logo />
        <div className="flex items-center gap-2">
          <Button onClick={copyLink} data-testid="preview-copy-link-btn" variant="outline" className="gap-2 rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/5">
            <Link2 className="h-4 w-4" /> Copia link
          </Button>
          <Button onClick={() => navigate(-1)} variant="ghost" size="icon" className="text-white/50 hover:bg-white/5 hover:text-white" data-testid="preview-close-btn">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Book stage */}
      <div className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="relative w-full max-w-lg" style={{ perspective: 1600 }}>
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={page}
              custom={dir}
              initial={{ rotateY: dir > 0 ? 45 : -45, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: dir > 0 ? -45 : 45, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformStyle: "preserve-3d" }}
              className="min-h-[70vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0A1124] shadow-2xl"
            >
              {p.type === "cover" && (
                <div className="flex h-full min-h-[70vh] flex-col">
                  {data.cover ? (
                    <div className="relative flex-1">
                      <img src={data.cover} alt={data.title} className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-transparent to-transparent" />
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center bg-[#050A15]">
                      <BookOpen className="h-16 w-16 text-[#D4AF37]/40" />
                    </div>
                  )}
                  <div className="p-8 text-center">
                    <h1 className="font-serif text-4xl font-light text-white">{data.title}</h1>
                    {data.subtitle && <p className="mt-2 text-white/50">{data.subtitle}</p>}
                    {data.author_name && <p className="mt-4 text-sm uppercase tracking-[0.3em] text-[#D4AF37]">{data.author_name}</p>}
                  </div>
                </div>
              )}

              {p.type === "synopsis" && (
                <div className="flex min-h-[70vh] flex-col justify-center p-12">
                  <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Sinossi</p>
                  <p className="mt-6 font-serif text-2xl font-light leading-relaxed text-white/90">{data.synopsis}</p>
                </div>
              )}

              {p.type === "chapter" && (
                <div className="min-h-[70vh] p-12">
                  <p className="font-mono text-xs text-[#D4AF37]">Capitolo {p.chapter.number}</p>
                  <h2 className="mt-3 font-serif text-3xl font-light text-white">{p.chapter.title}</h2>
                  <div className="mt-8 max-h-[45vh] overflow-y-auto pr-2">
                    <p className="whitespace-pre-wrap leading-relaxed text-white/70">{p.chapter.content}</p>
                  </div>
                </div>
              )}

              {p.type === "empty" && (
                <div className="flex min-h-[70vh] flex-col items-center justify-center p-12 text-center">
                  <BookOpen className="h-12 w-12 text-[#D4AF37]/40" />
                  <p className="mt-6 font-serif text-2xl font-light text-white">I capitoli approvati appariranno qui</p>
                  <p className="mt-2 text-white/40">Approva i capitoli per costruire l'anteprima del tuo libro.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-6 pb-10">
        <Button onClick={() => go(-1)} disabled={page === 0} data-testid="preview-prev-btn" variant="outline" size="icon" className="rounded-full border-white/10 bg-transparent text-white hover:bg-white/5 disabled:opacity-30">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="font-mono text-sm text-white/40">{page + 1} / {total}</span>
        <Button onClick={() => go(1)} disabled={page === total - 1} data-testid="preview-next-btn" variant="outline" size="icon" className="rounded-full border-white/10 bg-transparent text-white hover:bg-white/5 disabled:opacity-30">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
